import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v2AdminSetMcpTextSetMutation } from '@workspace/api-backend/react-query'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'

/**
 * The wording sets an admin can pin an investor to. A set is added here when its file is served by the
 * backend (`apps/backend/src/domain/mcp-text-sets`); the backend refuses an id it does not serve.
 */
export const MCP_TEXT_SET_OPTIONS = ['openai-S3c@1', 'openai-S3g@1', 'claude-K3@1'] as const

/** The select cannot hold an empty value, so "no pin" has a word of its own. */
const FOLLOW_THE_APP = 'app'

/**
 * Pins one investor's AI connection to a wording set, whatever app they use, to try a new version on a test
 * account before everyone gets it. "By app" removes the pin. An app shows new tool text only after it
 * refreshes its tool list; answers change at once.
 */
export function McpTextSetControl({ userId, textSet }: { userId: string; textSet: string | null }) {
  const queryClient = useQueryClient()
  const pin = useMutation({
    ...v2AdminSetMcpTextSetMutation(),
    onSuccess: () => queryClient.invalidateQueries(),
  })

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">AI wording</span>
      <Select
        value={textSet ?? FOLLOW_THE_APP}
        disabled={pin.isPending}
        onValueChange={(value) =>
          pin.mutate({
            path: { userId },
            body: { textSet: value === FOLLOW_THE_APP ? null : value },
          })
        }
      >
        <SelectTrigger className="h-8 w-[170px]" aria-label="AI wording">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={FOLLOW_THE_APP}>By app</SelectItem>
          {MCP_TEXT_SET_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {pin.isError && <span className="text-xs text-destructive">Could not save</span>}
    </div>
  )
}
